import { forwardRef, type Ref } from 'react';
import clsx from 'clsx';
import { useExperiment } from '@hungryroot/experiments';
import { OpenBoxNew } from '@hungryroot/icons';
import { CalloutBox } from '@hungryroot/pho';
import { Paragraph } from '@hungryroot/ramen';
import { PRECHECKOUT_PLAN_ROUTE } from '@hungryroot/routes';

import styles from './CalloutBoxSelector.module.css';

export const CALLOUT_BOX_COPY = {
  default: `You'll see your first cart with our recommendations after checkout. Edit and choose exactly what you want.`,
  payAsYouGo: `After checkout, see what we’ve picked just for you. Add, swap, or remove items — pay only for what you want.`,
};

export const CALLOUT_BOX_TEST_IDS = {
  default: 'default-callout-box',
  payAsYouGo: 'pay-as-you-go-callout-box',
};

export const CalloutBoxSelector = forwardRef(
  (props: { className?: string }, ref: Ref<HTMLDivElement>) => {
    // get the url's pathname
    const pathname = new URL(window.location.href).pathname;

    const { className } = props;
    const dietDrivenPreferencesVariant = useExperiment(175);
    const payAsYouGoVariant = useExperiment(173);

    const isDietDrivenPreferencesVariant =
      dietDrivenPreferencesVariant !== undefined &&
      dietDrivenPreferencesVariant > 0;

    const isPayAsYouGoVariant =
      payAsYouGoVariant !== undefined && payAsYouGoVariant > 0;

    const budgetRangePlanVariant = useExperiment(177);
    const isInBudgetRangePlanVariant =
      budgetRangePlanVariant !== undefined && budgetRangePlanVariant > 0;

    const isLoadingCalloutBox =
      dietDrivenPreferencesVariant === undefined ||
      budgetRangePlanVariant === undefined ||
      payAsYouGoVariant === undefined;

    const shouldHideCalloutBox =
      (pathname.includes(PRECHECKOUT_PLAN_ROUTE) &&
        isInBudgetRangePlanVariant) ||
      isDietDrivenPreferencesVariant;

    if (isLoadingCalloutBox || shouldHideCalloutBox) {
      return null;
    }

    // Default callout box
    let calloutBoxDataTestId = CALLOUT_BOX_TEST_IDS.default;
    let copy = CALLOUT_BOX_COPY.default;

    // Pay-as-you-go callout box
    if (isPayAsYouGoVariant) {
      calloutBoxDataTestId = CALLOUT_BOX_TEST_IDS.payAsYouGo;
      copy = CALLOUT_BOX_COPY.payAsYouGo;
    }

    return (
      <CalloutBox
        ref={ref}
        className={clsx(styles.box, className)}
        testid={calloutBoxDataTestId}
        image={
          <div data-testid="open-box-svg" className={styles['open-box']}>
            <OpenBoxNew />
          </div>
        }
      >
        <Paragraph className={styles.callout}>{copy}</Paragraph>
      </CalloutBox>
    );
  }
);
